/**
 * Entry point. Builds the engine context (bus + state machine), the game simulation, the renderer and
 * the HUD, wires cross-layer notification through the bus, and starts the fixed-step loop. Also owns
 * the FS-09 mode/hull selection flow — Mode Select → (Sector Select | Custom upload) → Hull Select →
 * Countdown — which is menu/profile logic, not simulation, so it lives here rather than in `Game`.
 */
import './ui/hud.css';
import { createEventBus } from './core/events';
import { createStateMachine, GameState } from './core/state-machine';
import { createLoop, type Renderer } from './core/loop';
import { Game } from './game/game';
import type { RunConfig, CustomTrackConfig } from './game/run';
import { MODES, resolvePlan, utcDateString, withOnboarding } from './game/modes/registry';
import { validateScript, ONBOARD_SKIP_OFFER_T, type OnboardingScript } from './content/onboarding';
import { MAX_STEMS } from './audio/synth';
import type { ModeId } from './game/modes/types';
import { HULLS, ALL_HULLS } from './player/hulls/hulls';
import type { HullId } from './player/hulls/types';
import { View } from './render/view';
import { Hud, type HowToPlayOpener } from './ui/hud';
import { Synth } from './audio/synth';
import { analyzeCustomTrack, CustomTrackError } from './audio/custom-track';
import { SECTORS, sectorByIndex } from './content/sectors';
import { applyPalette } from './render/palette';
import { hashSeed } from './core/rng';
import { ProfileStore, type RunSummary } from './meta/profile';
import { evaluateUnlocks, unlockDescription } from './meta/unlocks';

const canvas = document.getElementById('gl') as HTMLCanvasElement;

const bus = createEventBus();
const machine = createStateMachine(GameState.Boot, (_from, to) => {
  bus.emit('state:changed', { from: _from, to, simTick: 0 });
  onState(to);
});

const game = new Game(bus, machine);
const view = new View(canvas);
const hud = new Hud();
const synth = new Synth();
synth.init();
const profileStore = new ProfileStore();

game.setTraumaSink((a) => view.addTrauma(a));
game.attachInput(() => void synth.unlock());

// ── Renderer: draw + sync the HUD every frame ──────────────────────────────
const renderer: Renderer = {
  render(alpha: number): void {
    // Pause toggle (checked outside the sim so it works while paused).
    if (game.takePauseEdge()) {
      if (machine.current === GameState.Playing) machine.request(GameState.Paused, 'pause');
      else if (machine.current === GameState.Paused) machine.request(GameState.Countdown, 'resume');
    }

    view.render(alpha, {
      ship: game.ship,
      director: game.director,
      resonance: game.resonance,
      hazards: game.hazards,
      pickups: game.pickups.pool,
      playing: game.playing,
      decoys: game.powerups.decoys,
      echoPilot: game.powerups.echoPilot,
    });

    // FS-01 R10 / R15 — the glyph goes on the first steering input and never returns; the skip is
    // not offered before ONBOARD_SKIP_OFFER_T. Both are checked here rather than on the bus because
    // both are about elapsed state rather than about an event.
    if (game.inOnboarding) {
      if (!controlGlyphSpent && game.hasSteered) {
        controlGlyphSpent = true;
        hud.hideControlGlyph();
      }
      hud.setSkipVisible(game.audioClock.time >= skipOfferT, () => game.skipOnboarding());
    }

    if (machine.current === GameState.Playing || machine.current === GameState.Paused) {
      hud.setScore(game.resonance.score);
      hud.setMultiplier(game.resonance.multiplier);
      hud.setTier(game.director.tier);
      hud.setResonance(game.resonance.value, game.resonance.overdrive);
      hud.setPowerup(game.powerups.stored);
      hud.beat(Math.floor(game.audioClock.beat) % 4 & 3);
      if (game.bossActive) {
        hud.showBoss(
          game.boss.name,
          game.boss.phaseName,
          game.boss.phaseIndex,
          game.boss.phaseCount,
          game.boss.barsRemaining(game.audioClock),
          game.boss.incomingPhase(game.audioClock),
          game.boss.hudJam,
        );
        synth.setMusicLevel(game.boss.musicLevel);
      } else {
        hud.hideBoss();
        synth.setMusicLevel(1);
      }
    }
  },
};

const loop = createLoop({ bus, machine }, game, renderer);

// ── Mode/hull selection state (FS-09 §4, FS-10 §4.3) ────────────────────────
let pendingModeId: ModeId | null = null;
let pendingStartSector = 1;
let pendingCustom: CustomTrackConfig | null = null;
let currentConfig: RunConfig | null = null;

function randomSeed(): number {
  try {
    return crypto.getRandomValues(new Uint32Array(1))[0]!;
  } catch {
    return (Math.floor(performance.now()) ^ 0x9e3779b1) >>> 0;
  }
}

function genRunId(): string {
  try {
    return Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return String(Math.floor(performance.now()));
  }
}

const MODE_BLURB: Record<ModeId, string> = {
  'sector-run': 'The campaign — sectors 1 through 8, in order.',
  infinite: 'Every unlocked sector, chained end to end, looped forever. Ramps past the ceiling.',
  daily: 'One seeded attempt per UTC day. The same level for everyone.',
  gauntlet: 'Boss fights only, back to back, no breathing room.',
  zen: 'No death, no score — just the meter, the grazes and the music.',
  custom: 'Bring your own track. Tempo estimated on-device. Unranked.',
};

function showModeSelectScreen(): void {
  const profile = profileStore.get();
  const today = utcDateString(Date.now());
  const cards = (Object.keys(MODES) as ModeId[]).map((id) => {
    const m = MODES[id];
    const unlocked = profileStore.isUnlocked('mode', id);
    const dailyLocked = id === 'daily' && unlocked && profileStore.hasDailyAttempt(today);
    return {
      id,
      displayName: m.displayName,
      blurb: MODE_BLURB[id],
      unlocked,
      unlockHint: unlocked ? '' : unlockDescription({ kind: 'mode', id }),
      disabledReason: dailyLocked ? "TODAY'S ATTEMPT USED" : null,
    };
  });
  void profile; // profile is read via profileStore helpers above; kept for clarity of intent
  hud.showModeSelect(cards, chooseMode, () => machine.request(GameState.Menu, 'back'));
}

function chooseMode(id: ModeId): void {
  pendingModeId = id;
  pendingCustom = null;
  if (id === 'sector-run') {
    showSectorSelectScreen();
    return;
  }
  if (id === 'custom') {
    showCustomUploadScreen();
    return;
  }
  showHullSelectScreen();
}

function showSectorSelectScreen(): void {
  hud.showSectorSelect(
    SECTORS.map((s) => {
      const unlocked = profileStore.isUnlocked('sector', s.index);
      const maxUnlocked = Math.max(...profileStore.get().unlocked.sectors);
      return {
        index: s.index,
        name: s.name,
        genre: s.genre,
        bpm: s.bpm,
        bossName: s.bossName,
        unlocked,
        cleared: s.index < maxUnlocked,
        best: profileStore.best(`sector-run:sector-${s.index}`),
        primary: s.palette.primary,
        secondary: s.palette.secondary,
      };
    }),
    (index) => {
      pendingStartSector = index;
      showHullSelectScreen();
    },
    showModeSelectScreen,
  );
}

function showCustomUploadScreen(errorMessage?: string): void {
  hud.showCustomUpload(handleCustomFile, showModeSelectScreen, errorMessage);
}

function handleCustomFile(file: File): void {
  hud.showAnalyzing();
  void synth
    .unlock()
    .then(async () => {
      const ctx = synth.audioContext;
      if (!ctx) {
        showCustomUploadScreen('Audio is unavailable in this browser.');
        return;
      }
      const result = await analyzeCustomTrack(file, ctx);
      pendingCustom = {
        buffer: result.buffer,
        bpm: result.bpm,
        durationSeconds: result.buffer.duration,
        digest: result.digest,
        warning: result.warning,
      };
      showHullSelectScreen();
    })
    .catch((err: unknown) => {
      const msg = err instanceof CustomTrackError ? err.message : 'Could not analyse this file.';
      showCustomUploadScreen(msg);
    });
}

function showHullSelectScreen(): void {
  const cards = ALL_HULLS.map((id) => {
    const h = HULLS[id];
    const unlocked = profileStore.isUnlocked('hull', id);
    return {
      id,
      displayName: h.displayName,
      identity: h.identity,
      unlocked,
      unlockHint: unlocked ? '' : unlockDescription({ kind: 'hull', id }),
    };
  });
  hud.showHullSelect(cards, beginConfiguredRun, () => {
    if (pendingModeId === 'sector-run') showSectorSelectScreen();
    else if (pendingModeId === 'custom') showCustomUploadScreen();
    else showModeSelectScreen();
  });
}

function beginConfiguredRun(hullId: HullId): void {
  const modeId = pendingModeId!;
  const mode = MODES[modeId];
  const hull = HULLS[hullId];
  const profile = profileStore.get();
  const today = utcDateString(Date.now());

  let sectorList: readonly number[] = profile.unlocked.sectors;
  if (modeId === 'sector-run') {
    const filtered = profile.unlocked.sectors.filter((i) => i >= pendingStartSector);
    sectorList = filtered.length ? filtered : profile.unlocked.sectors;
  }
  let plan = resolvePlan(modeId, sectorList, today);
  // Onboarding is prepended to whatever plan the chosen mode produced, rather than being a mode of
  // its own (FS-09 R02 — one run orchestrator, and a mode contributes data, never a code path).
  if (shouldRunOnboarding()) {
    plan = withOnboarding(plan);
    onboardingReplayRequested = false;
  }

  let seed: number;
  let custom: CustomTrackConfig | undefined;
  if (modeId === 'daily') {
    seed = hashSeed(`daily:${today}`);
  } else if (modeId === 'custom' && pendingCustom) {
    seed = hashSeed(pendingCustom.digest);
    custom = pendingCustom;
  } else {
    seed = randomSeed();
  }

  startRun({ modeId, mode, hull, seed, plan, custom });
}

function startRun(config: RunConfig): void {
  currentConfig = config;
  game.beginRun(config, 0);
  machine.request(GameState.Countdown, 'start');
}

function retryRun(): void {
  if (!currentConfig) return;
  if (currentConfig.mode.retryAnchor === 'none') {
    showModeSelectScreen();
    return;
  }
  // R14 — onboarding runs **once per profile**, so Retry after a run that included it drops the
  // segment rather than replaying it. Retrying into the tutorial you just finished is the
  // "re-runnable on demand" clause read backwards: it makes it compulsory.
  if (currentConfig.plan[0]?.scriptId === 'onboarding' && !shouldRunOnboarding()) {
    currentConfig = { ...currentConfig, plan: currentConfig.plan.slice(1) };
    game.lastEndedSegmentIndex = Math.max(0, game.lastEndedSegmentIndex - 1);
  }
  const anchor = currentConfig.mode.retryAnchor === 'movement-start' ? game.lastEndedSegmentIndex : 0;
  game.beginRun(currentConfig, anchor);
  machine.request(GameState.Countdown, 'retry');
}

// ── Onboarding (FS-01 §5.2) ─────────────────────────────────────────────────
// The script is fetched, not imported: R38 wants it tunable without a rebuild and AC-20 asserts it
// is not inlined in the bundle. A failure here is not fatal — onboarding is skipped and the profile
// flag is left alone, so the next load tries again.
let onboardingScript: OnboardingScript | null = null;
let onboardingReplayRequested = false;
const skipOfferT = ONBOARD_SKIP_OFFER_T;

async function loadOnboardingScript(): Promise<void> {
  try {
    const res = await fetch('content/onboarding.json');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    onboardingScript = validateScript(await res.json());
    game.setOnboardingScript(onboardingScript);
    // The title card renders at boot, before this resolves, so "replay intro" would never appear
    // for a returning player. Re-render it if that is still the screen we are on.
    if (machine.current === GameState.Menu) showTitleScreen();
  } catch (err) {
    onboardingScript = null;
    game.setOnboardingScript(null);
    if (import.meta.env.DEV) console.warn('onboarding script unavailable — skipping onboarding', err);
  }
}

/** R14 — exactly once per profile, and re-runnable on demand. */
function shouldRunOnboarding(): boolean {
  if (!onboardingScript) return false;
  return onboardingReplayRequested || !profileStore.flag('onboardingCompleted');
}

// ── How to Play (FS-12 R34–R36, FS-01 R40–R42) ──────────────────────────────
// There is no `ScreenRouter` in this build — FS-12 §3.1 is unimplemented and screens are
// `GameState` plus `Hud.show*` calls. One opener and a re-render is the honest size of R24's focus
// stack here; building a router for one screen would be the larger mistake.
let lastResults: Parameters<Hud['showResults']>[0] | null = null;

function openHowToPlay(from: HowToPlayOpener): void {
  hud.showHowToPlay(
    {
      useInput: game.touchPrimary ? 'the USE control' : 'Space',
      moveInput: game.touchPrimary ? 'Drag anywhere' : 'Arrow keys or WASD',
      // FS-12 F20 — opening the manual mid-attempt does not consume it, and a player will assume
      // the worst unless told otherwise.
      dailyAttemptLive: from === 'paused' && currentConfig?.modeId === 'daily',
    },
    () => {
      if (from === 'title') showTitleScreen();
      else if (from === 'paused') showPausedScreen();
      else if (lastResults) hud.showResults(lastResults);
    },
  );
}

function showTitleScreen(): void {
  // R14 — "replay intro" is only offered to a profile that has already seen it.
  hud.showMenuScreen(showModeSelectScreen, profileStore.flag('onboardingCompleted') && !!onboardingScript);
}

function showPausedScreen(): void {
  hud.showPaused(
    () => machine.request(GameState.Countdown, 'resume'),
    () => game.abandonRun(),
  );
}

// ── State routing ──────────────────────────────────────────────────────────
function onState(to: GameState): void {
  switch (to) {
    case GameState.Menu:
      synth.stopMusic();
      synth.stopCustomTrack();
      showTitleScreen();
      break;
    case GameState.Countdown:
      synth.resume();
      // Near-instant countdown → Playing.
      machine.request(GameState.Playing, 'go');
      break;
    case GameState.Playing:
      hud.showHud();
      hud.setScoreVisible(currentConfig?.mode.rules.scoreEnabled ?? true);
      // R13 — during onboarding the HUD starts empty and earns its elements. Everywhere else it is
      // complete from the first frame: the reveal state belongs to the segment, not the profile.
      hud.setProgressive(game.inOnboarding);
      if (game.inOnboarding) {
        stemsEarned = 1;
        controlGlyphSpent = false;
        synth.setStems(1);
      } else {
        synth.setStems(MAX_STEMS);
      }
      if (currentConfig?.modeId === 'daily') profileStore.markDailyAttempted(utcDateString(Date.now()));
      break;
    case GameState.Paused:
      synth.suspend();
      showPausedScreen();
      break;
    case GameState.Results: {
      synth.stopMusic();
      synth.stopCustomTrack();
      synth.sfx(game.won ? 'overdrive' : 'death');
      hud.hideBoss();

      const cfg = currentConfig;
      // A run that ended inside onboarding is not a score. It must not set a best and must not name
      // a sector: "Sector 0" is not a place, and 22 points for dying in the tutorial would be the
      // first entry on an otherwise real leaderboard.
      const ranked = (cfg?.mode.rankable ?? true) && !game.endedDuringOnboarding;
      const sectorIndex = game.sector.index > 0 ? game.sector.index : null;
      let isBest = false;
      if (cfg) {
        const summary: RunSummary = {
          runId: genRunId(),
          at: Date.now(),
          modeId: cfg.modeId,
          hullId: cfg.hull.id,
          sectorIndex,
          sectorCleared: game.highestSectorCleared,
          score: game.resonance.score,
          won: game.won,
          hazardsCleared: game.hazardsCleared,
          beatSteers: game.resonance.beatSteersCount,
          hitsTaken: game.hitsTakenCount,
          overdrives: game.resonance.overdrivesThisRun,
          multiplierPeak: game.resonance.multiplierPeak,
          ranked,
          abandoned: game.deathCause === 'Abandoned',
        };
        if (cfg.modeId === 'custom' && cfg.custom) {
          isBest = profileStore.recordCustomBest(cfg.custom.digest, summary.score);
          profileStore.recordRun(summary, false);
        } else {
          isBest = profileStore.recordRun(summary, ranked);
        }
        for (const g of evaluateUnlocks(profileStore.get())) {
          if (g.kind === 'hull') profileStore.grantHull(g.id);
          else if (g.kind === 'sector') profileStore.grantSector(g.id);
          else profileStore.grantMode(g.id);
        }
        profileStore.flush();
      }

      // R36/R41 — offered once, at the end of a profile's first run, beside Retry with equal
      // weight. The flag is set on *offer*, not on open: a player who saw it and chose Retry has
      // been offered it, and asking again would be the nagging R41 exists to forbid.
      const offerHowToPlay = !profileStore.flag('howToPlayOffered');
      if (offerHowToPlay) profileStore.setFlag('howToPlayOffered', true);

      lastResults = {
        modeId: cfg?.modeId ?? 'sector-run',
        modeName: cfg?.mode.displayName ?? '',
        hullName: cfg?.hull.displayName ?? '',
        score: cfg?.mode.rules.scoreEnabled ?? true ? game.resonance.score : null,
        peakResonance: game.resonance.peakResonance,
        overdriveTime: game.resonance.overdriveTime,
        hazardsCleared: game.hazardsCleared,
        staggerCount: game.staggerCount,
        cause: game.deathCause || 'run ended',
        won: game.won,
        ranked,
        isBest,
        sectorName: game.sector.name,
        sectorIndex,
        offerHowToPlay,
      };
      hud.showResults(lastResults);
      break;
    }
    default:
      break;
  }
}

hud.setHandlers({
  onRetry: retryRun,
  onMenu: () => machine.request(GameState.Menu, 'back'),
  onModeSelect: showModeSelectScreen,
  onHowToPlay: openHowToPlay,
  onReplayIntro: () => {
    onboardingReplayRequested = true;
    showModeSelectScreen();
  },
});

// ── segment/palette/music (FS-09 §4.3, FS-11) ───────────────────────────────
bus.on('segment:changed', ({ sectorIndex, bpm }) => {
  if (currentConfig?.custom) {
    void synth.unlock().then(() => synth.playCustomTrack(currentConfig!.custom!.buffer));
    return;
  }
  const sector = sectorByIndex(sectorIndex);
  applyPalette(sector.palette);
  view.applySectorPalette();
  void synth.unlock().then(() => synth.startMusic(bpm));
});

// ── SFX bus wiring ───────────────────────────────────────────────────────────
bus.on('player:graze', () => synth.sfx('graze'));
bus.on('player:nodeCentre', () => synth.sfx('node'));
bus.on('player:hit', (p) => synth.sfx(p.fatal ? 'death' : 'hit'));
bus.on('overdrive:start', () => {
  synth.setOverdrive(true);
  synth.sfx('overdrive');
});
bus.on('overdrive:end', () => synth.setOverdrive(false));
bus.on('powerup:stored', () => synth.sfx('pickup'));
bus.on('powerup:activated', () => synth.sfx('pickup'));

// FS-07 R37 — name the power-up *and* how to use it, at the slot, non-blocking. The input is named
// for the device in the player's hands: on a phone "Space" is a lie.
bus.on('pickup:collected', (p) => {
  hud.namePickup(p.powerup, game.touchPrimary ? 'USE' : 'SPACE', p.firstEver);
});
// R38 — a pickup that passes uncollected still teaches, via a falling-pitch miss cue.
bus.on('pickup:missed', () => synth.sfx('miss'));

// ── Onboarding: teaching devices (FS-01 §5.3) ───────────────────────────────
// Every device here is diegetic and text-free (R09). `stem-add` is the one with a standing
// obligation: §5.3 requires a **visual equivalent** on every stem, so muted play loses nothing —
// here that is the HUD reveal the same cue carries, plus the hull emissive step below.
let stemsEarned = 1;
let controlGlyphSpent = false;

bus.on('onboarding:cue', ({ devices, phase }) => {
  for (const d of devices) {
    switch (d) {
      case 'stem-add':
        if (phase === 'arrive' && stemsEarned < MAX_STEMS) {
          stemsEarned++;
          synth.setStems(stemsEarned);
          // The visual twin: a trauma-free emissive pulse, so a muted player sees the reward.
          view.addTrauma(0.06);
        }
        break;
      case 'control-glyph':
        if (!controlGlyphSpent) hud.showControlGlyph(game.touchPrimary);
        break;
      case 'camera-lean':
        // Bias toward the node this cue is about. Cosmetic, and disabled under reduced motion by
        // `View` itself so no call site can forget (§5.3).
        view.setNodeLean(Math.sin(game.nodeLeanTheta) * 6, Math.cos(game.nodeLeanTheta) * 6, 1);
        break;
      case 'haptic':
        if (phase === 'arrive') navigator.vibrate?.(18);
        break;
      case 'graze-flare':
        if (phase === 'arrive') synth.sfx('graze');
        break;
      default:
        // beat-pulse, node-highlight, funnel, ghost-line, pickup-lure, slot-pull and
        // polarity-mirror are properties of the geometry or of the always-on renderer, not
        // one-shot effects: the script asks for them by authoring the hazard that way.
        break;
    }
  }
  if (phase === 'arrive' && !devices.includes('camera-lean')) view.clearNodeLean();
});

// R13 — the element appears when its mechanic becomes relevant, not before.
bus.on('onboarding:reveal', ({ element }) => hud.reveal(element));

// R08 — the global phantom → lethal transition. The visual half (hollow outline becoming solid
// fill) is in `hazard-view.ts`; this is the filter sweep that announces it (§5.2 bar 9.1).
bus.on('onboarding:lethal', () => synth.filterSweep());

bus.on('onboarding:end', ({ reason }) => {
  hud.setProgressive(false);
  hud.setSkipVisible(false);
  hud.hideControlGlyph();
  view.clearNodeLean();
  synth.setStems(MAX_STEMS);
  // R14 — completed, skipped, or died: all three mean the player has met the game. Only a script
  // that never loaded leaves the flag alone, and that path never emits this event.
  void reason;
  profileStore.setFlag('onboardingCompleted', true);
  profileStore.flush();
});


// ── Resize ───────────────────────────────────────────────────────────────────
window.addEventListener('resize', () => view.resize());
window.addEventListener('pagehide', () => profileStore.flush());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') profileStore.flush();
});

// ── Boot ─────────────────────────────────────────────────────────────────────
const boot = document.getElementById('boot');
if (boot) boot.classList.add('hidden');
machine.request(GameState.Menu, 'boot-complete');
loop.start();
// Non-blocking: the menu is up and playable before this resolves. A first run that begins in the
// half-second before the script lands simply gets no onboarding, which is the same outcome as a
// failed fetch and is preferable to a loading gate on the front door (FS-01 R06, T_FIRST_INPUT).
void loadOnboardingScript();

if (import.meta.env.DEV) {
  (window as unknown as { __sw: unknown }).__sw = { machine, loop, game, profileStore };
}
